import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Camera, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Camera-based barcode scanner for the pantry / shopping list.
 *
 * Props:
 *   open, onOpenChange
 *   onDetected({ code, product })  – called once a barcode is decoded.
 *                                    `product` may be null if OpenFoodFacts has no record.
 *   lookup (boolean, default true) – fetch product info from OpenFoodFacts after decode
 *
 * Uses html5-qrcode which supports EAN-13, EAN-8, UPC-A, UPC-E, Code128, QR, etc.
 */
export default function BarcodeScannerDialog({ open, onOpenChange, onDetected, lookup = true }) {
  const containerRef = useRef(null);
  const scannerRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | starting | scanning | looking-up | error
  const [errorMsg, setErrorMsg] = useState("");
  const lastCodeRef = useRef(""); // prevent duplicate triggers from successive frames

  // Start/stop scanner with dialog open state
  useEffect(() => {
    let cancelled = false;
    if (!open) {
      stopScanner();
      return;
    }
    (async () => {
      setStatus("starting");
      setErrorMsg("");
      lastCodeRef.current = "";
      try {
        // Dynamically import to keep the bundle lean
        const mod = await import("html5-qrcode");
        if (cancelled) return;
        const { Html5Qrcode } = mod;
        const id = "barcode-scanner-region";
        // Wait one tick so the DOM node is present
        await new Promise((r) => setTimeout(r, 0));
        if (cancelled) return;
        const scanner = new Html5Qrcode(id, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 260, height: 160 },
            aspectRatio: 1.4,
          },
          (decodedText) => handleDecoded(decodedText),
          () => { /* per-frame failures are normal; ignore */ }
        );
        if (!cancelled) setStatus("scanning");
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setErrorMsg(err?.message || "Kunne ikke starte kamera. Sjekk at du har gitt tilgang.");
      }
    })();
    return () => {
      cancelled = true;
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stopScanner = async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (!s) return;
    try {
      if (s.isScanning) await s.stop();
      await s.clear();
    } catch { /* ignore */ }
  };

  const handleDecoded = async (code) => {
    if (!code) return;
    if (code === lastCodeRef.current) return;
    lastCodeRef.current = code;
    setStatus("looking-up");
    try {
      // Stop the camera immediately so no extra frames trigger; then look up
      await stopScanner();
    } catch { /* ignore */ }

    let product = null;
    if (lookup) {
      try {
        const r = await fetch(`https://world.openfoodfacts.org/api/v2/product/${encodeURIComponent(code)}.json`, {
          headers: { "User-Agent": "PantryBuddy/1.0 (https://pantrybuddy.no)" },
        });
        if (r.ok) {
          const j = await r.json();
          if (j?.status === 1 && j.product) {
            const p = j.product;
            product = {
              name: p.product_name_no || p.product_name || p.generic_name_no || p.generic_name || "",
              brand: (p.brands || "").split(",")[0]?.trim() || "",
              quantity: p.quantity || "",
              image: p.image_small_url || p.image_url || "",
            };
          }
        }
      } catch { /* network errors → product stays null */ }
    }
    onDetected?.({ code, product });
    onOpenChange(false);
    if (!product) {
      toast.info(`Strekkode lest: ${code} (ingen treff i database)`);
    } else {
      toast.success(`${product.name || code} skannet`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) stopScanner(); onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <Camera className="h-5 w-5 text-navy-800" /> Skann strekkode
          </DialogTitle>
          <DialogDescription>
            Hold strekkoden innanfor ramma. Kamera lukkast automatisk når koden er lesen.
          </DialogDescription>
        </DialogHeader>
        <div className="relative bg-stone-900 rounded-lg overflow-hidden aspect-[4/3]" data-testid="barcode-scanner-stage">
          <div id="barcode-scanner-region" ref={containerRef} className="w-full h-full" />
          {(status === "starting" || status === "looking-up") && (
            <div className="absolute inset-0 flex items-center justify-center bg-stone-900/80 text-white text-sm gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {status === "starting" ? "Startar kamera…" : "Søker etter produkt…"}
            </div>
          )}
          {status === "error" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-900/90 text-white text-sm p-4 text-center" data-testid="barcode-scanner-error">
              <X className="h-6 w-6 mb-2" />
              <div className="font-semibold mb-1">Kunne ikke starte kamera</div>
              <div className="text-xs opacity-90">{errorMsg}</div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between text-xs text-stone-500 mt-2">
          <span>Støttar EAN-13/EAN-8/UPC + QR</span>
          <button
            onClick={() => onOpenChange(false)}
            className="px-3 py-1.5 rounded-md text-stone-600 hover:bg-stone-100"
            data-testid="barcode-scanner-cancel"
          >
            Avbryt
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
